import { Button, formatDate, Modal } from '@evaneos/b2b-ui/legacy';
import { Form, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { DayPickerFormInput } from '../../../../../../design-system/Form/DayPickerFormInput';
import {
    postInstalmentRequest,
    putInstalmentRequest,
} from '../../../../../../resources/contract/actions';
import { instalmentValidationSchema } from '../../../../../../resources/contract/operations';
import { ContractId, InstalmentViewModel } from '../../../../../../resources/contract/types';
import { AllTranslationKeys } from '../../../../../../shared/intl/types';
import { useAppDispatch } from '../../../../../../shared/redux';
import { getToday } from '../../../../../../shared/utilities/date';
import { NumberFormInput } from '../NumberFormInput';
import { TextFormInput } from '../TextFormInput';

import s from './InstalmentModalActions.module.css';

export type Props = {
    contractId: ContractId;
    isOpen: boolean;
    title: AllTranslationKeys;
    submitTextButton: AllTranslationKeys;
    instalment: InstalmentViewModel;
    close: () => void;
    submitAction: typeof postInstalmentRequest | typeof putInstalmentRequest;
};

export const InstalmentModalActions = ({
    contractId,
    isOpen,
    title,
    submitTextButton,
    instalment,
    close,
    submitAction,
}: Props): JSX.Element => {
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const today = getToday();
    const handleSubmit = (values: InstalmentViewModel) => {
        dispatch(submitAction(contractId, values));
        close();
    };

    return (
        <Formik
            initialValues={instalment}
            validationSchema={instalmentValidationSchema}
            onSubmit={(values: InstalmentViewModel) => {
                dispatch(submitAction(contractId, values));
                close();
            }}
        >
            {({ values, isValid, setFieldValue }) => (
                <Modal
                    isOpen={isOpen}
                    title={intl.formatMessage({
                        id: title,
                    })}
                    mainButton={
                        <Button
                            priority="primary"
                            type="submit"
                            disabled={!isValid}
                            onClick={() => handleSubmit(values)}
                        >
                            <FormattedMessage id={submitTextButton} />
                        </Button>
                    }
                    secondaryButton={
                        <Button priority="secondary" onClick={close}>
                            <FormattedMessage id="form.cancel" />
                        </Button>
                    }
                >
                    <Form className={s.instalmentForm}>
                        <TextFormInput
                            name="payerName"
                            label={intl.formatMessage({
                                id: 'contract.payment_schedule.instalment.payer_name',
                            })}
                            wrapperClassName={s.inputContainer}
                        />
                        <TextFormInput
                            name="label"
                            label={intl.formatMessage({
                                id: 'contract.payment_schedule.instalment.label',
                            })}
                            wrapperClassName={s.inputContainer}
                        />
                        <DayPickerFormInput
                            name="due-date"
                            label={intl.formatMessage({
                                id: 'contract.payment_schedule.instalment.due_date',
                            })}
                            onDayChange={(date) => {
                                if (!date) {
                                    return;
                                }

                                void setFieldValue('dueDate', date);
                            }}
                            fromDate={today}
                            format="dd/MM/yyyy"
                            inputProps={{
                                value: values.dueDate
                                    ? formatDate(values.dueDate, 'dd/MM/yyyy')
                                    : '',
                                // @ts-expect-error data-testid is not in inputProps type but works at runtime
                                'data-testid': 'dueDate',
                            }}
                        />
                        <NumberFormInput
                            name="price.amount"
                            label={'contract.payment_schedule.instalment.price'}
                            containerClassName={s.inputContainer}
                            currency={values.price.currency}
                        />
                    </Form>
                </Modal>
            )}
        </Formik>
    );
};
